/**
 * API модуля Prompthub — prompt-templates и categories (согласно Swagger)
 */
import apiReq from 'core/api';

// ─── Типы (схемы из Swagger) ─────────────────────────────────────────────

export interface PromptTemplateCategory {
  id: string;
  title: string;
  color: string;
  description?: string;
}

export interface PromptTemplateListingCategory {
  id: string;
  title: string;
  color: string;
}

export interface PromptTemplate {
  id: string;
  title: string;
  description?: string;
  content: string;
  favorite_count: number;
  created_by: string;
  created_at: number;
  updated_at: number;
  categories: PromptTemplateListingCategory[];
  is_favorite: boolean;
}

export interface PromptTemplatesListResponse {
  count: number;
  items: PromptTemplate[];
}

export interface PromptTemplateSaveForm {
  title: string;
  description?: string;
  content: string;
  category_ids: string[];
}

export interface PromptTemplateCategorySaveForm {
  title: string;
  color: string;
  description?: string;
}

export interface PromptTemplateSearchParams {
  query?: string;
  created_by?: string;
  category_id?: string;
  case_id?: string;
  limit?: number;
  offset?: number;
  sort_column?: 'created_at' | 'favorite_count' | 'title';
  sort_direction?: 'ASC' | 'DESC';
}

export interface ToggleFavoriteResponse {
  status: string;
  is_favorite: boolean;
  favorite_count: number;
}

// ─── Категории ────────────────────────────────────────────────────────────

/** GET /prompt-templates/categories — Список категорий */
export const getCategories = async (): Promise<PromptTemplateCategory[]> => {
  const data = await apiReq<PromptTemplateCategory[]>('prompt-templates/categories');
  return Array.isArray(data) ? data : [];
};

/** POST /prompt-templates/categories/create — Создать категорию */
export const createCategory = async (
  form: PromptTemplateCategorySaveForm
): Promise<PromptTemplateCategory> => {
  const data = await apiReq<PromptTemplateCategory>(
    'prompt-templates/categories/create',
    'POST',
    form
  );
  if (!data) throw new Error('Empty response');
  return data;
};

/** POST /prompt-templates/categories/{uuid}/update — Обновить категорию */
export const updateCategory = async (
  uuid: string,
  form: PromptTemplateCategorySaveForm
): Promise<PromptTemplateCategory> => {
  const data = await apiReq<PromptTemplateCategory>(
    `prompt-templates/categories/${uuid}/update`,
    'POST',
    form
  );
  if (!data) throw new Error('Empty response');
  return data;
};

/** DELETE /prompt-templates/categories/{uuid}/delete — Удалить категорию */
export const deleteCategory = async (uuid: string): Promise<void> => {
  await apiReq(`prompt-templates/categories/${uuid}/delete`, 'DELETE');
};

// ─── Шаблоны промптов ─────────────────────────────────────────────────────

/** GET /prompt-templates — Список шаблонов с пагинацией */
export const getPromptTemplates = async (
  params?: PromptTemplateSearchParams
): Promise<PromptTemplatesListResponse> => {
  const search = new URLSearchParams();
  if (params?.query) search.set('query', params.query);
  if (params?.created_by) search.set('created_by', params.created_by);
  if (params?.category_id) search.set('category_id', params.category_id);
  if (params?.case_id) search.set('case_id', params.case_id);
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  if (params?.sort_column) search.set('sort_column', params.sort_column);
  if (params?.sort_direction) search.set('sort_direction', params.sort_direction);

  const query = search.toString();
  const path = query ? `prompt-templates?${query}` : 'prompt-templates';
  const data = await apiReq<PromptTemplatesListResponse>(path);
  return data ?? { count: 0, items: [] };
};

/** GET /prompt-templates/personal — Список своих шаблонов */
export const getPersonalPromptTemplates = async (
  params?: Pick<PromptTemplateSearchParams, 'limit' | 'offset'>
): Promise<PromptTemplatesListResponse> => {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));

  const query = search.toString();
  const path = query ? `prompt-templates/personal?${query}` : 'prompt-templates/personal';
  const data = await apiReq<PromptTemplatesListResponse>(path);
  return data ?? { count: 0, items: [] };
};

/** POST /prompt-templates/create — Создать шаблон */
export const createPromptTemplate = async (
  form: PromptTemplateSaveForm
): Promise<PromptTemplate> => {
  const data = await apiReq<PromptTemplate>('prompt-templates/create', 'POST', form);
  if (!data) throw new Error('Empty response');
  return data;
};

/** POST /prompt-templates/{id}/update — Обновить шаблон */
export const updatePromptTemplate = async (
  id: string,
  form: PromptTemplateSaveForm
): Promise<PromptTemplate> => {
  const data = await apiReq<PromptTemplate>(`prompt-templates/${id}/update`, 'POST', form);
  if (!data) throw new Error('Empty response');
  return data;
};

/** DELETE /prompt-templates/{id}/delete — Удалить шаблон */
export const deletePromptTemplate = async (id: string): Promise<void> => {
  await apiReq(`prompt-templates/${id}/delete`, 'DELETE');
};

/** POST /prompt-templates/{id}/toggle-favorite — Переключить лайк */
export const togglePromptTemplateFavorite = async (id: string): Promise<ToggleFavoriteResponse> => {
  const data = await apiReq<ToggleFavoriteResponse>(
    `prompt-templates/${id}/toggle-favorite`,
    'POST'
  );
  if (!data) throw new Error('Empty response');
  return data;
};

// ─── Кейсы шаблонов промптов (согласно Swagger) ───────────────────────────

export interface PromptTemplateCaseAssignmentListing {
  template_id: string;
  title: string;
  sorting_order: number;
}

export interface PromptTemplateCase {
  id: string;
  title: string;
  description?: string;
  role_ids: string[];
  prompt_templates: PromptTemplateCaseAssignmentListing[];
}

export interface PromptTemplateCaseListing {
  id: string;
  title: string;
  description?: string;
}

export interface PromptTemplateCasesListResponse {
  count: number;
  items: PromptTemplateCaseListing[];
}

export interface PromptTemplateCaseSaveForm {
  title: string;
  description?: string;
  role_ids: string[];
}

/** GET /prompt-templates/cases — Список кейсов */
export const getPromptTemplateCases = async (params?: {
  role_id?: number;
}): Promise<PromptTemplateCasesListResponse> => {
  const search = new URLSearchParams();
  if (params?.role_id != null) search.set('role_id', String(params.role_id));
  const query = search.toString();
  const path = query ? `prompt-templates/cases?${query}` : 'prompt-templates/cases';
  const data = await apiReq<PromptTemplateCasesListResponse>(path);
  return data ?? { count: 0, items: [] };
};

/** GET /prompt-templates/cases/{uuid}/view — Полный кейс */
export const getPromptTemplateCase = async (uuid: string): Promise<PromptTemplateCase> => {
  const data = await apiReq<PromptTemplateCase>(`prompt-templates/cases/${uuid}/view`, 'GET');
  if (!data) throw new Error('Кейс не найден');
  return data;
};

/** POST /prompt-templates/cases/create — Создать кейс */
export const createPromptTemplateCase = async (
  form: PromptTemplateCaseSaveForm
): Promise<PromptTemplateCase> => {
  const data = await apiReq<PromptTemplateCase>('prompt-templates/cases/create', 'POST', form);
  if (!data) throw new Error('Empty response');
  return data;
};

/** POST /prompt-templates/cases/{uuid}/update — Обновить кейс */
export const updatePromptTemplateCase = async (
  uuid: string,
  form: PromptTemplateCaseSaveForm
): Promise<PromptTemplateCase> => {
  const data = await apiReq<PromptTemplateCase>(
    `prompt-templates/cases/${uuid}/update`,
    'POST',
    form
  );
  if (!data) throw new Error('Empty response');
  return data;
};

/** POST /prompt-templates/cases/{uuid}/templates/add — Добавить шаблон в кейс */
export const addTemplateToCase = async (
  caseUuid: string,
  templateId: string
): Promise<PromptTemplateCase> => {
  const data = await apiReq<PromptTemplateCase>(
    `prompt-templates/cases/${caseUuid}/templates/add`,
    'POST',
    { template_id: templateId }
  );
  if (!data) throw new Error('Empty response');
  return data;
};

/** POST /prompt-templates/cases/{uuid}/templates/reorder — Изменить порядок шаблона */
export const reorderTemplateInCase = async (
  caseUuid: string,
  templateId: string,
  sortingOrder: number
): Promise<PromptTemplateCase> => {
  const data = await apiReq<PromptTemplateCase>(
    `prompt-templates/cases/${caseUuid}/templates/reorder`,
    'POST',
    { template_id: templateId, sorting_order: sortingOrder }
  );
  if (!data) throw new Error('Empty response');
  return data;
};

/** POST /prompt-templates/cases/{uuid}/templates/remove — Удалить шаблон из кейса */
export const removeTemplateFromCase = async (
  caseUuid: string,
  templateId: string
): Promise<PromptTemplateCase> => {
  const data = await apiReq<PromptTemplateCase>(
    `prompt-templates/cases/${caseUuid}/templates/remove`,
    'POST',
    { template_id: templateId }
  );
  if (!data) throw new Error('Empty response');
  return data;
};

/** DELETE /prompt-templates/cases/{uuid}/delete — Удалить кейс */
export const deletePromptTemplateCase = async (uuid: string): Promise<void> => {
  await apiReq(`prompt-templates/cases/${uuid}/delete`, 'DELETE');
};
